import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { formatCurrency } from '../CoreFunctions';
import ToDoModal from '../components/ToDoModal';

const Home = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [financialsLoading, setFinancialsLoading] = useState(false);
  const [error, setError] = useState('');
  const [period, setPeriod] = useState('YTD'); // 1M, 3M, 6M, YTD, 1Y

  // Data states
  const [bsData, setBsData] = useState({ assets: 0, liabilities: 0, equity: 0 });
  const [isData, setIsData] = useState({ income: 0, expenses: 0, net: 0 });
  const [cfData, setCfData] = useState({ inflow: 0, outflow: 0, net: 0 });
  const [invRet, setInvRet] = useState({ portfolio: 0, benchmark: 0, alpha: 0 });
  const [todos, setTodos] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState(['all']);
  const [selectedStatementStatuses, setSelectedStatementStatuses] = useState(['all']);
  const [statementData, setStatementData] = useState([]);
  const [statementLoading, setStatementLoading] = useState(false);
  const [currentCalendarDate, setCurrentCalendarDate] = useState(new Date());

  const [isToDoModalOpen, setIsToDoModalOpen] = useState(false);
  const [editingTodo, setEditingTodo] = useState(null);

  // Upcoming Payments state
  const [upcomingPayments, setUpcomingPayments] = useState([]);
  const [upcomingPaymentsLoading, setUpcomingPaymentsLoading] = useState(false);
  const [paymentCalendarDate, setPaymentCalendarDate] = useState(new Date());

  // Date range calculation
  const { startStr, endStr } = useMemo(() => {
    const end = new Date();
    const start = new Date();
    
    if (period === '1M') {
      start.setMonth(end.getMonth() - 1);
    } else if (period === '3M') {
      start.setMonth(end.getMonth() - 3);
    } else if (period === '6M') {
      start.setMonth(end.getMonth() - 6);
    } else if (period === 'YTD') {
      start.setMonth(0, 1); // Jan 1st
    } else if (period === '1Y') {
      start.setFullYear(end.getFullYear() - 1);
    }

    return {
      startStr: start.toISOString().slice(0, 10),
      endStr: end.toISOString().slice(0, 10)
    };
  }, [period]);

  const num = (val) => {
    if (val == null) return 0;
    if (typeof val === 'number') return val;
    if (typeof val === 'string') return parseFloat(val) || 0;
    if (typeof val === 'object' && val.amount != null) return parseFloat(val.amount) || 0;
    return 0;
  };

  const fetchTodos = useCallback(async (assetMap) => {
    try {
      if (!assetMap) {
        // If assetMap is not provided, we need to fetch asset lists first to build it
        const [physListRes, resiListRes, vehicleListRes] = await Promise.all([
          axios.get('/api/balance_sheet/assets/physical_assets/'),
          axios.get('/api/balance_sheet/assets/physical_assets/resi_properties/'),
          axios.get('/api/vehicles/'),
        ]);

        assetMap = new Map();
        (vehicleListRes.data || []).forEach(v => {
           let label = v.label || (v.vehicle_year && v.vehicle_make && v.vehicle_model ? `${v.vehicle_year} ${v.vehicle_make} ${v.vehicle_model}` : null);
           if (label) assetMap.set(v.id, label);
        });
        (resiListRes.data || []).forEach(a => {
           if (!assetMap.has(a.id)) {
             let label = a.label || a.name;
             if (!label && a.address) label = a.address.street_address;
             if (label) assetMap.set(a.id, label);
           }
        });
        (physListRes.data || []).forEach(a => {
          if (!assetMap.has(a.id) || assetMap.get(a.id).startsWith('Asset ')) {
            let label = a.label || a.name;
            assetMap.set(a.id, label || `Asset ${a.id}`);
          }
        });
      }

      // Fetch To-Do's
      const todoRes = await axios.get('/api/balance_sheet/assets/physical_assets/todos/');
      const todosWithLabels = (todoRes.data || []).map(t => ({
        ...t,
        assetLabel: assetMap.get(t.asset) || `Asset ${t.asset}`
      }));
      setTodos(todosWithLabels);
    } catch (err) {
      console.error('Home todos load error', err);
    }
  }, []);

  // Split data fetching: Todos (once on mount) and Financials (when period/dates change)
  useEffect(() => {
    const fetchStaticData = async () => {
      try {
        const [physListRes, resiListRes, vehicleListRes] = await Promise.all([
          axios.get('/api/balance_sheet/assets/physical_assets/'),
          axios.get('/api/balance_sheet/assets/physical_assets/resi_properties/'),
          axios.get('/api/vehicles/'),
        ]);

        const assetMap = new Map();
        (vehicleListRes.data || []).forEach(v => {
           let label = v.label || (v.vehicle_year && v.vehicle_make && v.vehicle_model ? `${v.vehicle_year} ${v.vehicle_make} ${v.vehicle_model}` : null);
           if (label) assetMap.set(v.id, label);
        });
        (resiListRes.data || []).forEach(a => {
           if (!assetMap.has(a.id)) {
             let label = a.label || a.name;
             if (!label && a.address) label = a.address.street_address;
             if (label) assetMap.set(a.id, label);
           }
        });
        (physListRes.data || []).forEach(a => {
          if (!assetMap.has(a.id) || assetMap.get(a.id).startsWith('Asset ')) {
            let label = a.label || a.name;
            assetMap.set(a.id, label || `Asset ${a.id}`);
          }
        });

        // Store assetMap for potential re-fetches
        window._assetMap = assetMap;

        await fetchTodos(assetMap);
      } catch (err) {
        console.error('Home static data load error', err);
      } finally {
        setLoading(false);
      }
    };
    fetchStaticData();
  }, [fetchTodos]);

  useEffect(() => {
    const fetchStatementData = async (force = false) => {
      try {
        const today = new Date().toISOString().slice(0, 10);
        const lastFetch = localStorage.getItem('lastStatementFetch');
        const cached = localStorage.getItem('statementDataCache');
        
        if (force || lastFetch !== today || !cached) {
          setStatementLoading(true);
          const res = await axios.get('/api/statement-calendar-data/');
          setStatementData(res.data);
          localStorage.setItem('lastStatementFetch', today);
          localStorage.setItem('statementDataCache', JSON.stringify(res.data));
          setStatementLoading(false);
        } else {
          if (cached) {
            setStatementData(JSON.parse(cached));
          }
        }
      } catch (err) {
        console.error('Statement data load error', err);
        setStatementLoading(false);
      }
    };
    fetchStatementData();

    // Attach to window for manual trigger if needed
    window.refreshStatements = () => fetchStatementData(true);
  }, []);

  useEffect(() => {
    const fetchUpcomingPayments = async () => {
      setUpcomingPaymentsLoading(true);
      try {
        const year = paymentCalendarDate.getFullYear();
        const month = paymentCalendarDate.getMonth() + 1;
        const res = await axios.get(`/api/upcoming-payments-data/?year=${year}&month=${month}`);
        setUpcomingPayments(res.data);
      } catch (err) {
        console.error('Upcoming payments load error', err);
      } finally {
        setUpcomingPaymentsLoading(false);
      }
    };
    fetchUpcomingPayments();
  }, [paymentCalendarDate]);

  const changePaymentMonth = (delta) => {
    setPaymentCalendarDate(prev => {
      const next = new Date(prev);
      next.setMonth(next.getMonth() + delta);
      return next;
    });
  };

  useEffect(() => {
    const fetchFinancialData = async () => {
      setFinancialsLoading(true);
      try {
        // 1. Balance Sheet - We want the LATEST snapshot (current)
        const [physHist, invHist, cashHist] = await Promise.all([
          axios.get('/api/balance_sheet/assets/physical_assets/market_values/history/'),
          axios.get('/api/invested_assets/accounts/values/history/'),
          axios.get('/api/balance_sheet/assets/cash_accounts/balances/history/'),
        ]);

        const getLatest = (histData) => {
          let total = 0;
          (histData || []).forEach(it => {
            if (it.series && it.series.length > 0) {
              // Sort by date desc
              const sorted = [...it.series].sort((a, b) => b.date.localeCompare(a.date));
              total += num(sorted[0].value);
            }
          });
          return total;
        };

        const assets = getLatest(physHist.data) + getLatest(invHist.data) + getLatest(cashHist.data);
        
        const finAnal = await axios.get('/api/balance_sheet/financing/analytics/', { 
          params: { group_by: 'month' } 
         });
        const periods = finAnal.data?.periods || [];
        const latestIdx = periods.length - 1;
        let liabilities = 0;
        if (latestIdx >= 0) {
          const byType = finAnal.data.by_type || {};
          Object.values(byType).forEach(typeData => {
            liabilities += num(typeData.outstanding?.[latestIdx]);
          });
        }
        
        const getAtDate = (histData, targetDateStr) => {
           let total = 0;
           (histData || []).forEach(it => {
              if (it.series && it.series.length > 0) {
                 // Sort descending to find the LATEST point before or on target
                 const sorted = [...it.series].sort((a, b) => b.date.localeCompare(a.date));
                 const match = sorted.find(p => p.date <= targetDateStr);
                 if (match) total += num(match.value);
              }
           });
           return total;
        };

        const startAssets = getAtDate(physHist.data, startStr) + getAtDate(invHist.data, startStr) + getAtDate(cashHist.data, startStr);
        let startLiabilities = 0;
        const startIdx = periods.findIndex(p => p >= startStr.slice(0, 7));
        if (startIdx >= 0) {
           const byType = finAnal.data.by_type || {};
           Object.values(byType).forEach(typeData => {
              startLiabilities += num(typeData.outstanding?.[startIdx]);
           });
        }
        
        setBsData({ 
           assets, liabilities, equity: assets - liabilities,
           deltaAssets: assets - startAssets,
           deltaLiabilities: liabilities - startLiabilities,
           deltaEquity: (assets - liabilities) - (startAssets - startLiabilities)
        });

        // 2. Income Statement - For the selected period
        const [rvTxns, caTxns] = await Promise.all([
          axios.get('/api/balance_sheet/financing/revolvers/transactions/', { params: { start_date: startStr, end_date: endStr }  }),
          axios.get('/api/balance_sheet/assets/cash_accounts/transactions/', { params: { start_date: startStr, end_date: endStr }  }),
        ]);

        const allTxns = [...(rvTxns.data?.transactions || []), ...(caTxns.data?.transactions || [])];
        let income = 0;
        let expenses = 0;

        const isIncomeCategory = (cat, t = null) => {
          const c = (cat || '').toLowerCase();
          if (['income', 'income_recurring', 'income_nonrecurring', 'tax_refund', 'interest_income', 'dividend_income'].includes(c)) return true;
          if (t && t.linked_income_stream_id) return true;
          return false;
        };

        allTxns.forEach(t => {
          const process = (item) => {
            const cat = (item.expense_category || '').toLowerCase();
            const amt = num(item.amount);
            
            if (isIncomeCategory(cat, item)) {
              income += Math.abs(amt);
            } else {
              // Not income
              const isCash = (caTxns.data?.transactions || []).some(ct => ct.id === t.id);
              const linkedType = item.linked_debt_info?.type || null;
              const isLoanPayment = (cat === 'payments') && (linkedType === 'auto_loan' || linkedType === 'mortgage' || linkedType === 'heloc');

              if (isCash) {
                // Cash Account
                if (isLoanPayment) {
                  expenses += Math.abs(amt); // payments are negative in cash account
                } else if (cat !== 'payments' && cat !== 'transfer') {
                  if (amt < 0) expenses += Math.abs(amt);
                }
              } else {
                // Revolver (Credit Card)
                if (cat !== 'payments' && cat !== 'transfer' && cat !== 'rewards_cashback') {
                  if (amt > 0) expenses += amt;
                }
              }
            }
          };

          if (t.children && t.children.length > 0) {
            t.children.forEach(process);
          } else if (!t.parent) {
            process(t);
          }
        });
        setIsData({ income, expenses, net: income - expenses });

        // 3. Cash Flow - For the selected period
        let inflow = 0;
        let outflow = 0;
        
        (caTxns.data?.transactions || []).forEach(t => {
          const process = (item) => {
            const amt = num(item.amount);
            const cat = (item.expense_category || '').toLowerCase();
            if (cat === 'transfer') return;
            // For cash accounts, we exclude non-debt payments to avoid double counting 
            // CC spending. But we SHOULD include debt payments (mortgages, etc.)
            const linkedType = item.linked_debt_info?.type || null;
            const isLoanPayment = (cat === 'payments') && (linkedType === 'auto_loan' || linkedType === 'mortgage' || linkedType === 'heloc');
            
            if (cat === 'payments' && !isLoanPayment) return;

            if (amt > 0) inflow += amt; else outflow += Math.abs(amt);
          };

          if (t.children && t.children.length > 0) {
            t.children.forEach(process);
          } else if (!t.parent) {
            process(t);
          }
        });
        // Add CC spend (outflow) and refunds (inflow)
        (rvTxns.data?.transactions || []).forEach(t => {
          const process = (item) => {
            const amt = num(item.amount);
            const cat = (item.expense_category || '').toLowerCase();
            // Exclude payments and transfers on CC side as they are captured on the cash side
            if (cat === 'payments' || cat === 'transfer') return;
            
            if (amt > 0) outflow += amt; else inflow += Math.abs(amt);
          };

          if (t.children && t.children.length > 0) {
            t.children.forEach(process);
          } else if (!t.parent) {
            process(t);
          }
        });
        setCfData({ inflow, outflow, net: inflow - outflow });
        
        // 4. Investment Returns - Portfolio vs Benchmark (for pf=1, bm=1)
        try {
          const invRes = await axios.get('/api/invested_assets/returns/portfolio_vs_benchmark/', {
            params: { portfolio_id: 1, benchmark_id: 1, start: startStr, end: endStr }
           });
          const { portfolio, benchmark } = invRes.data || {};
          const pRet = num(portfolio?.twr);
          const bRet = num(benchmark?.twr);
          setInvRet({ portfolio: pRet, benchmark: bRet, alpha: pRet - bRet });
        } catch (invErr) {
          console.error('Investment returns load error', invErr);
          // Set zeros on failure to avoid stale data
          setInvRet({ portfolio: 0, benchmark: 0, alpha: 0 });
        }
      } catch (err) {
        console.error('Home financial data load error', err);
        setError('Failed to load financial data.');
      } finally {
        setFinancialsLoading(false);
      }
    };

    fetchFinancialData();
  }, [startStr, endStr]);

  const containerStyle = {
    background: '#1e293b',
    borderRadius: '12px',
    padding: '20px',
    marginBottom: '20px',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
  };

  const metricBoxStyle = {
    flex: 1,
    padding: '10px',
    textAlign: 'center',
  };

  const metricValueStyle = {
    fontSize: '1.25rem',
    fontWeight: 'bold',
    color: '#f8fafc',
  };

  const metricLabelStyle = {
    fontSize: '0.875rem',
    color: '#94a3b8',
    textTransform: 'uppercase',
    letterSpacing: '0.025em',
  };

  const filteredTodos = useMemo(() => {
    if (selectedStatuses.includes('all')) return todos;
    return todos.filter(t => selectedStatuses.includes(t.status));
  }, [todos, selectedStatuses]);

  const handleStatusToggle = (status) => {
    if (status === 'all') {
      setSelectedStatuses(['all']);
    } else {
      let newStatuses = selectedStatuses.filter(s => s !== 'all');
      if (newStatuses.includes(status)) {
        newStatuses = newStatuses.filter(s => s !== status);
        if (newStatuses.length === 0) newStatuses = ['all'];
      } else {
        newStatuses.push(status);
      }
      setSelectedStatuses(newStatuses);
    }
  };

  const handleTodoStatusChange = async (todoId, newStatus) => {
    try {
      await axios.patch(`/api/balance_sheet/assets/physical_assets/todo-items/${todoId}/`,  { status: newStatus });
      fetchTodos(window._assetMap);
    } catch (err) {
      console.error('Error updating todo status:', err);
    }
  };

  const handleEditTodo = (todo) => {
    setEditingTodo(todo);
    setIsToDoModalOpen(true);
  };

  const handleStatementStatusToggle = (status) => {
    if (status === 'all') {
      setSelectedStatementStatuses(['all']);
    } else {
      let newStatuses = selectedStatementStatuses.filter(s => s !== 'all');
      if (newStatuses.includes(status)) {
        newStatuses = newStatuses.filter(s => s !== status);
        if (newStatuses.length === 0) newStatuses = ['all'];
      } else {
        newStatuses.push(status);
      }
      setSelectedStatementStatuses(newStatuses);
    }
  };

  const renderFinancialSection = (title, metrics, path, isPercent = false) => (
    <div style={{ marginBottom: '15px', borderBottom: '1px solid #334155', paddingBottom: '10px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }} onClick={() => navigate(path)}>
        <h3 style={{ fontSize: '1.1rem', color: '#e2e8f0', margin: 0 }}>{title}</h3>
        <span style={{ fontSize: '0.8rem', color: '#64748b' }}>View Details →</span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '10px' }}>
        {metrics.map((m, i) => (
          <div key={i} style={metricBoxStyle}>
            <div style={metricLabelStyle}>{m.label}</div>
            <div style={{ ...metricValueStyle, color: m.color || '#f8fafc' }}>
              {isPercent ? (
                `${m.value >= 0 ? '+' : ''}${(m.value * 100).toFixed(2)}%`
              ) : (
                formatCurrency(m.value, 0)
              )}
            </div>
            {m.delta !== undefined && (
              <div style={{ fontSize: '0.75rem', color: m.delta >= 0 ? (m.label === 'Liabilities' ? '#f87171' : '#4ade80') : (m.label === 'Liabilities' ? '#4ade80' : '#f87171') }}>
                {m.delta >= 0 ? '+' : ''}{formatCurrency(m.delta, 0)}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="page" style={{ maxWidth: '1200px', margin: '0 auto', padding: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h1 style={{ margin: 0 }}>Dashboard</h1>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(450px, 1fr))', gap: '20px' }}>
        {/* Container 1: Financial Aggregate View */}
        <div style={containerStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px', borderBottom: '2px solid #3b82f6' }}>
            <h2 style={{ fontSize: '1.25rem', color: '#f1f5f9', margin: 0, paddingBottom: '8px' }}>Financial Summary</h2>
            <div style={{ display: 'flex', gap: '4px' }}>
              {['1M', '3M', '6M', 'YTD', '1Y'].map(p => (
                <button
                  key={p}
                  className={`btn btn-sm ${period === p ? 'btn-primary' : 'btn-outline-light'}`}
                  style={{ fontSize: '0.7rem', padding: '2px 6px' }}
                  onClick={() => setPeriod(p)}
                >
                  {p}
                </button>
              ))}
            </div>
          </div>
          {financialsLoading ? (
            <div style={{ color: '#94a3b8' }}>Loading...</div>
          ) : (
            <>
              {renderFinancialSection('Balance Sheet', [
                { label: 'Total Assets', value: bsData.assets, delta: bsData.deltaAssets },
                { label: 'Liabilities', value: bsData.liabilities, color: '#f87171', delta: bsData.deltaLiabilities },
                { label: 'Equity', value: bsData.equity, color: '#4ade80', delta: bsData.deltaEquity }
              ], '/balance_sheet')}
              
              {renderFinancialSection('Income Statement', [
                { label: 'Total Income', value: isData.income, color: '#4ade80' },
                { label: 'Expenses', value: isData.expenses, color: '#f87171' },
                { label: 'Net Income', value: isData.net }
              ], '/income_statement')}

              {renderFinancialSection('Cash Flow', [
                { label: 'Cash Inflow', value: cfData.inflow, color: '#4ade80' },
                { label: 'Outflow', value: cfData.outflow, color: '#f87171' },
                { label: 'Net Cash', value: cfData.net }
              ], '/cash_flow')}

              {renderFinancialSection('Investment Returns', [
                { label: 'Portfolio', value: invRet.portfolio },
                { label: 'Benchmark', value: invRet.benchmark },
                { label: 'Alpha', value: invRet.alpha, color: invRet.alpha >= 0 ? '#4ade80' : '#f87171' }
              ], '/balance_sheet/assets/invested_assets', true)}
            </>
          )}
        </div>

        {/* Container 2: To-Do's */}
        <div style={containerStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', borderBottom: '2px solid #3b82f6', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', paddingBottom: '8px' }}>
              <h2 style={{ fontSize: '1.25rem', color: '#f1f5f9', margin: 0 }}>To-Do List</h2>
              <div style={{ display: 'flex', gap: '4px' }}>
                {['all', 'not_started', 'in_progress', 'completed'].map(s => (
                  <button
                    key={s}
                    className={`btn btn-sm ${selectedStatuses.includes(s) ? 'btn-primary' : 'btn-outline-light'}`}
                    style={{ fontSize: '0.65rem', padding: '1px 6px', borderRadius: '10px', textTransform: 'capitalize' }}
                    onClick={() => handleStatusToggle(s)}
                  >
                    {s.replace('_', ' ')}
                  </button>
                ))}
              </div>
            </div>
            {filteredTodos.length > 0 && (
              <div style={{ color: '#fbbf24', fontSize: '0.9rem', fontWeight: 'bold', textAlign: 'right', paddingBottom: '8px' }}>
                Total Est. Cost: {formatCurrency(filteredTodos.reduce((acc, t) => acc + num(t.expected_cost), 0), 0)}
              </div>
            )}
          </div>
          {loading ? (
            <div style={{ color: '#94a3b8' }}>Loading...</div>
          ) : (
            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
              {filteredTodos.length === 0 ? (
                <p style={{ color: '#94a3b8' }}>No items found.</p>
              ) : (
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {filteredTodos.map(todo => (
                    <li key={todo.id} style={{ padding: '12px', borderBottom: '1px solid #334155', display: 'flex', flexDirection: 'column' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontWeight: '600', color: '#f1f5f9' }}>{todo.title}</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <button 
                            onClick={() => handleEditTodo(todo)}
                            className="btn btn-sm btn-outline-primary" 
                            style={{ fontSize: '0.65rem', padding: '1px 6px', height: 'fit-content' }}
                          >
                            Edit
                          </button>
                          <div style={{ position: 'relative' }}>
                            <select 
                              value={todo.status} 
                              onChange={(e) => handleTodoStatusChange(todo.id, e.target.value)}
                              style={{ 
                                fontSize: '0.75rem', 
                                padding: '2px 24px 2px 8px', 
                                borderRadius: '12px', 
                                background: todo.status === 'completed' ? '#1e40af' : 
                                           todo.status === 'in_progress' ? '#065f46' : '#92400e',
                                color: '#fff',
                                border: 'none',
                                cursor: 'pointer',
                                appearance: 'none',
                                fontWeight: '500',
                                textAlign: 'center'
                              }}
                            >
                              <option value="not_started">Not Started</option>
                              <option value="in_progress">In-Progress</option>
                              <option value="delayed_on_hold">Delayed/On-Hold</option>
                              <option value="completed">Completed</option>
                            </select>
                            <span style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', fontSize: '0.6rem' }}>▼</span>
                          </div>
                        </div>
                      </div>
                      <span style={{ fontSize: '0.8rem', color: '#94a3b8', marginTop: '4px' }}>{todo.assetLabel} | Priority: {todo.priority}</span>
                      {todo.expected_cost > 0 && (
                        <span style={{ fontSize: '0.8rem', color: '#fbbf24', marginTop: '2px' }}>Est. Cost: {formatCurrency(todo.expected_cost)}</span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        {/* Container 3: Upcoming Payments */}
        <div style={containerStyle}>
          <div style={{ marginBottom: '20px', borderBottom: '2px solid #3b82f6' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '8px' }}>
              <h2 style={{ fontSize: '1.25rem', color: '#f1f5f9', margin: 0 }}>Upcoming Payments</h2>
              <div style={{ color: '#fbbf24', fontSize: '0.9rem', fontWeight: 'bold', textAlign: 'right' }}>
                {(() => {
                  const total = upcomingPayments.reduce((acc, p) => acc + (p.amount || 0), 0);
                  const now = new Date();
                  now.setHours(0, 0, 0, 0);
                  const remaining = upcomingPayments.reduce((acc, p) => {
                    const pDate = new Date(p.date + 'T12:00:00');
                    pDate.setHours(0, 0, 0, 0);
                    return pDate >= now ? acc + (p.amount || 0) : acc;
                  }, 0);
                  return (
                    <>
                      <div>Total: {formatCurrency(total, 0)}</div>
                      <div style={{ fontSize: '0.75rem', opacity: 0.9 }}>Remaining: {formatCurrency(remaining, 0)}</div>
                    </>
                  );
                })()}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', justifyContent: 'center', paddingBottom: '8px' }}>
              <button className="btn btn-sm btn-outline-light" style={{ padding: '0px 8px' }} onClick={() => changePaymentMonth(-1)}>&lt;</button>
              <span style={{ color: '#f1f5f9', fontSize: '0.9rem', minWidth: '100px', textAlign: 'center' }}>
                {paymentCalendarDate.toLocaleString('default', { month: 'long', year: 'numeric' })}
              </span>
              <button className="btn btn-sm btn-outline-light" style={{ padding: '0px 8px' }} onClick={() => changePaymentMonth(1)}>&gt;</button>
            </div>
          </div>
          
          <div style={{ height: '160px', marginBottom: '15px', position: 'relative' }}>
            {upcomingPaymentsLoading ? (
              <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b' }}>Loading...</div>
            ) : upcomingPayments.length === 0 ? (
              <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b', border: '1px dashed #334155', borderRadius: '8px' }}>No payments found</div>
            ) : (
              <>
                <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', height: '100%', padding: '0 10px', gap: '2px', position: 'relative' }}>
                  {(() => {
                    const now = new Date();
                    const isCurrentMonth = paymentCalendarDate.getFullYear() === now.getFullYear() && paymentCalendarDate.getMonth() === now.getMonth();
                    const currentDay = now.getDate();
                    const daysInMonth = new Date(paymentCalendarDate.getFullYear(), paymentCalendarDate.getMonth() + 1, 0).getDate();
                    const dailyTotals = Array.from({ length: daysInMonth }, (_, i) => ({ day: i + 1, total: 0, utilityTotal: 0, debtTotal: 0 }));
                    upcomingPayments.forEach(p => {
                      const d = new Date(p.date + 'T12:00:00').getDate();
                      if (d >= 1 && d <= daysInMonth) {
                        dailyTotals[d - 1].total += p.amount;
                        if (p.type === 'Utility') {
                          dailyTotals[d - 1].utilityTotal += p.amount;
                        } else {
                          dailyTotals[d - 1].debtTotal += p.amount;
                        }
                      }
                    });
                    const maxTotal = Math.max(...dailyTotals.map(d => d.total), 1);
                    return (
                      <>
                        {isCurrentMonth && (
                          <div 
                            style={{ 
                              position: 'absolute', 
                              left: `calc(10px + ${(currentDay - 0.5) / daysInMonth * 100}% - ${(currentDay - 0.5) / daysInMonth * 20}px)`,
                              top: 0, 
                              bottom: 0, 
                              width: '0', 
                              borderLeft: '1px dashed #ef4444', 
                              zIndex: 10,
                              pointerEvents: 'none'
                            }} 
                          />
                        )}
                        {dailyTotals.map(d => (
                          <div key={d.day} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%', justifyContent: 'flex-end' }}>
                            <div 
                              title={`Day ${d.day}: ${formatCurrency(d.total)} (Debt: ${formatCurrency(d.debtTotal)}, Utility: ${formatCurrency(d.utilityTotal)})`}
                              style={{ 
                                width: '100%', 
                                height: `${(d.total / maxTotal) * 100}%`, 
                                background: d.total > 0 ? (d.debtTotal > 0 && d.utilityTotal > 0 ? 'linear-gradient(to top, #3b82f6 50%, #10b981 50%)' : (d.utilityTotal > 0 ? '#10b981' : '#3b82f6')) : '#1e293b',
                                minHeight: d.total > 0 ? '2px' : '0',
                                borderRadius: '2px 2px 0 0',
                                position: 'relative',
                                zIndex: 5
                              }} 
                            />
                          </div>
                        ))}
                      </>
                    );
                  })()}
                </div>
                {/* X-axis labels */}
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0 10px', marginTop: '5px' }}>
                  {(() => {
                    const daysInMonth = new Date(paymentCalendarDate.getFullYear(), paymentCalendarDate.getMonth() + 1, 0).getDate();
                    return Array.from({ length: daysInMonth }, (_, i) => i + 1).map(d => (
                      <div key={d} style={{ flex: 1, textAlign: 'center', fontSize: '0.65rem', color: '#94a3b8' }}>
                        {[7, 14, 21, 28].includes(d) ? d : ''}
                      </div>
                    ));
                  })()}
                </div>
              </>
            )}
          </div>

          <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {upcomingPayments.sort((a, b) => a.date.localeCompare(b.date)).map((p, i) => (
                <li key={i} style={{ padding: '8px 0', borderBottom: '1px solid #334155', display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem' }}>
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <span style={{ color: p.type === 'Utility' ? '#10b981' : '#f1f5f9', fontWeight: '500' }}>{p.account_name}</span>
                    <span style={{ color: '#94a3b8', fontSize: '0.75rem' }}>{p.date} ({p.type})</span>
                  </div>
                  <div style={{ color: p.type === 'Utility' ? '#10b981' : '#f1f5f9', fontWeight: '600', paddingRight: '10px' }}>{formatCurrency(p.amount, 0)}</div>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Container 4: Statement Data */}
        <div style={containerStyle}>
          <div style={{ marginBottom: '20px', borderBottom: '2px solid #3b82f6' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', paddingBottom: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <h2 style={{ fontSize: '1.25rem', color: '#f1f5f9', margin: 0 }}>Statement Data</h2>
                <div style={{ display: 'flex', gap: '4px' }}>
                  {['all', 'loaded', 'missed', 'projected'].map(s => (
                    <button
                      key={s}
                      className={`btn btn-sm ${selectedStatementStatuses.includes(s) ? 'btn-primary' : 'btn-outline-light'}`}
                      style={{ fontSize: '0.65rem', padding: '1px 6px', borderRadius: '10px', textTransform: 'capitalize' }}
                      onClick={() => handleStatementStatusToggle(s)}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
              <button 
                className="btn btn-sm btn-outline-info" 
                style={{ fontSize: '0.65rem', padding: '2px 6px' }} 
                onClick={() => window.refreshStatements()}
                disabled={statementLoading}
              >
                {statementLoading ? '...' : 'Refresh'}
              </button>
            </div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', justifyContent: 'center', paddingBottom: '8px' }}>
              <button className="btn btn-sm btn-outline-light" style={{ padding: '0px 8px' }} onClick={() => setCurrentCalendarDate(new Date(currentCalendarDate.getFullYear(), currentCalendarDate.getMonth() - 1, 1))}>&lt;</button>
              <span style={{ color: '#f1f5f9', fontSize: '0.9rem', minWidth: '100px', textAlign: 'center' }}>
                {currentCalendarDate.toLocaleString('default', { month: 'long', year: 'numeric' })}
              </span>
              <button className="btn btn-sm btn-outline-light" style={{ padding: '0px 8px' }} onClick={() => setCurrentCalendarDate(new Date(currentCalendarDate.getFullYear(), currentCalendarDate.getMonth() + 1, 1))}>&gt;</button>
            </div>
          </div>
          <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {statementLoading ? (
              <div style={{ color: '#94a3b8', textAlign: 'center', padding: '20px' }}>Loading statement predictions...</div>
            ) : (() => {
              const month = currentCalendarDate.getMonth();
              const year = currentCalendarDate.getFullYear();
              const targetPeriod = `${year}-${String(month + 1).padStart(2, '0')}`;
              
              const monthlyData = statementData.filter(s => s.stmt_period === targetPeriod);
              const filtered = monthlyData.filter(s => {
                if (selectedStatementStatuses.includes('all')) return true;
                return selectedStatementStatuses.includes(s.status);
              }).sort((a, b) => a.date.localeCompare(b.date));

              if (filtered.length === 0) {
                return <p style={{ color: '#94a3b8', textAlign: 'center' }}>No statements match the selected criteria.</p>;
              }

              return (
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {filtered.map((s, i) => {
                    const lender = s.lender_name || 'Unknown';
                    const type = s.obligation_type || (s.account_type === 'Revolver' ? 'Credit Card' : s.account_type) || 'Account';
                    const detail = s.detail || '';
                    const expected = s.date;
                    const loaded = s.loaded_date;
                    
                    let displayString = `${lender} - ${type}`;
                    if (detail) {
                      const separator = s.account_type === 'Utility' ? ' - ' : ': ';
                      displayString += `${separator}${detail}`;
                    }
                    displayString += ` -> expected: ${expected}`;
                    if (s.status === 'loaded' && loaded) {
                      displayString += ` -> loaded: ${loaded}`;
                    }

                    // console.log('Rendering item:', { lender, type, detail, account_type: s.account_type, displayString });

                    return (
                      <li key={i} style={{ padding: '10px', borderBottom: '1px solid #334155', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          <span style={{ fontWeight: '600', color: '#f1f5f9', fontSize: '0.85rem' }}>
                            {displayString}
                          </span>
                        </div>
                        <span style={{ 
                          fontSize: '0.65rem', 
                          padding: '2px 8px', 
                          borderRadius: '12px', 
                          background: s.status === 'loaded' ? '#065f46' : s.status === 'projected' ? '#1e40af' : '#991b1b',
                          color: '#fff',
                          textTransform: 'capitalize',
                          marginLeft: '10px'
                        }}>
                          {s.status}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              );
            })()}
          </div>
          <div style={{ marginTop: '15px', display: 'flex', gap: '15px', fontSize: '0.7rem', justifyContent: 'center' }}>
             <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><div style={{ width: '8px', height: '8px', background: '#065f46', borderRadius: '50%' }}></div><span style={{ color: '#94a3b8' }}>Loaded</span></div>
             <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><div style={{ width: '8px', height: '8px', background: '#1e40af', borderRadius: '50%' }}></div><span style={{ color: '#94a3b8' }}>Projected</span></div>
             <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><div style={{ width: '8px', height: '8px', background: '#991b1b', borderRadius: '50%' }}></div><span style={{ color: '#94a3b8' }}>Missed</span></div>
          </div>
        </div>
      </div>
      <ToDoModal 
        isOpen={isToDoModalOpen} 
        onClose={() => setIsToDoModalOpen(false)} 
        todo={editingTodo} 
        onSave={() => fetchTodos(window._assetMap)}
      />
    </div>
  );
};

export default Home;
